import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  QaiButtonComponent,
  QaiIconComponent,
  QaiInnerWrapComponent,
  QaiFloatingSelectComponent,
  TableRow,
  QaiTabsComponent,
  QaiTabDirective,
  QaiFloatingSelectOption,
} from '@qai/ui';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthorReactiveFormService } from '../../services/author-reactive-form.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { AssignmentsComponent } from './assignments/assignments.component';
import { AssignmentSimulationsComponent } from './simulations/simulations.component';
import { ActionModalComponent } from '../../components/modals/simulation-assignment-action-modal/action-modal.component';
import { DeleteModalComponent } from '../../components/modals/delete-modal/delete-modal.component';
import { ISimulation } from 'src/app/interfaces/self-serve/responses/isimulation';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AssignmentModalComponent } from '../../components/modals/assignment-modal/assignment-modal.component';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/requests/iassignment';
import { IAssignmentCreate } from 'src/app/interfaces/self-serve/assignment-manager/responses/iassignment-create';
import {
  toDateInputString,
  toIsoDateString,
} from 'src/app/utils/date-parser.util';
import { AssignmentReactiveFormService } from '../../services/assignment-reactive-form.service';
import { ButtonFloatingSelectComponent } from '../../components/button-floating-select/button-floating-select.component';
import { AddSimulationModalComponent } from '../../components/modals/add-simulation-to-assignment/add-simulation-to-assignment.component';
import {
  EntityActionType,
  EntityType,
} from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { AddUserToAssignmentModalComponent } from '../../components/modals/add-users-to-assignment/add-users-to-assignment-modal.component';
import { IAccountInfo } from 'src/app/interfaces/iaccount-info';
import { AssignmentConstants } from 'src/app/utils/constants/self-serve/assignment.constants';
import { IUsersWithTeams } from 'src/app/interfaces/self-serve/assignment-manager/responses/iusers-with-teams';
import { TeamUserParserService } from '../../services/team-user-parser.service';
import { ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/assignments.service';
import { SIMULATION_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/author.service';
import { IConfirmActionPayload } from 'src/app/interfaces/self-serve/requests/iconfirm-action-payload';

@Component({
  selector: 'assignments-dashboard',
  standalone: true,
  templateUrl: './assignments-dashboard.component.html',
  styleUrls: ['./assignments-dashboard.component.scss'],
  imports: [
    CommonModule,
    QaiInnerWrapComponent,
    QaiIconComponent,
    QaiButtonComponent,
    QaiFloatingSelectComponent,
    QaiTabsComponent,
    QaiTabDirective,
    AssignmentsComponent,
    AssignmentSimulationsComponent,
    ActionModalComponent,
    DeleteModalComponent,
    AssignmentModalComponent,
    ButtonFloatingSelectComponent,
    AddSimulationModalComponent,
    AddUserToAssignmentModalComponent,
  ],
})
export class AssignmentsDashboardComponent implements OnInit, OnChanges {
  @ViewChild(AssignmentsComponent)
  assignmentsComponent!: AssignmentsComponent;
  
  @ViewChild(QaiTabsComponent)
  tabsComponent!: QaiTabsComponent;
  
  EntityType = EntityType;
  EntityActionType = EntityActionType;

  simulationToDelete: { id: string | number; rowData: TableRow } | null = null;
  simulationToDuplicate: { id: string; name: string; moduleUuid?: string } | null = null;
  isLoading = false;
  selectedAccount: { label: string; value: string } | undefined;
  networkRequestInProgress: boolean = false;
  assignmentRequestInProgress: boolean = false;
  selectedAccountUuid: string = '';
  isDeleteModalOpen: boolean = false;
  simulationsRefreshKey = 0;
  isAssignmentModalVisible: boolean = false;
  isUserAssignmentModalVisible: boolean = false;
  addSimulationsToAssignmentModalOpen: boolean = false;
  moduleAssignmentUuid: string = '';
  moduleUuid: string = '';
  modalErrorStatus?: number;
  createModuleOptions = [
    { label: 'Assignment', value: 'assignment' },
    { label: 'Simulation', value: 'simulation' },
  ];
  deleteContext: {
    type: EntityType.SIMULATION | EntityType.ASSIGNMENT;
    id: string | number;
    rowData: TableRow;
    moduleUuid?: string;
  } | null = null;
  duplicateAssignmentModalVisible = false;
  assignmentToDuplicateName = '';
  assignmentToDuplicateId = '';
  hasSingleEligibleAccount = false;

  modalOpen = false;
  modalEntityType: EntityType = EntityType.SIMULATION;
  modalActionType: EntityActionType = EntityActionType.CREATE;
  modalOriginalName = '';

  @Input() assignmentsTabAccounts: IAccountInfo[] = [];
  @Input() simulationsTabAccounts: IAccountInfo[] = [];

  currentTab: 'assignments' | 'simulations' =
    AssignmentConstants.ASSIGNMENTS_TAB;

  shouldShowAssignmentsTab: boolean = false;
  shouldShowSimulationsTab: boolean = false;

  selectedAccountHasAuthor: boolean = false;
  selectedAccountHasAssignmentManager: boolean = false;

  assignmentsTabOptions: { label: string; value: string }[] = [];
  simulationsTabOptions: { label: string; value: string }[] = [];

  @ViewChild(AssignmentModalComponent)
  assignmentModalComponent!: AssignmentModalComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private assignmentService: AssignmentsService,
    private cdRef: ChangeDetectorRef,
    private authorReactiveFormService: AuthorReactiveFormService,
    private assignmentReactiveFormService: AssignmentReactiveFormService,
    public teamUserParserService: TeamUserParserService
  ) {}

  ngOnInit() {
    this.hasSingleEligibleAccount = SelfServeLocalStorageUtil.hasSingleEligibleAccount();
    this.determineUserRoles();
    this.setInitialTab();
    this.prepareAccountOptions();
    this.setSelectedAccount();
    
    if (this.route?.snapshot?.queryParamMap) {
      this.checkQueryParameters();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['assignmentsTabAccounts'] ||
      changes['simulationsTabAccounts']
    ) {
      this.determineUserRoles();
      this.prepareAccountOptions();
      this.setSelectedAccount();
      
      if (this.route?.snapshot?.queryParamMap) {
        this.checkQueryParameters();
      }
    }
  }

  get options(): { label: string; value: string }[] {
    return this.currentTab === AssignmentConstants.ASSIGNMENTS_TAB
      ? this.assignmentsTabOptions
      : this.simulationsTabOptions;
  }

  private determineUserRoles(): void {
    this.shouldShowAssignmentsTab = this.assignmentsTabAccounts.length > 0;
    this.shouldShowSimulationsTab = this.simulationsTabAccounts.length > 0;
  }

  private determineSelectedAccountRoles(): void {
    if (!this.selectedAccountUuid) {
      this.selectedAccountHasAuthor = false;
      this.selectedAccountHasAssignmentManager = false;
      return;
    }

    const accountRoles = SelfServeLocalStorageUtil.getSelectedAccountRoles(
      this.selectedAccountUuid
    );
    this.selectedAccountHasAuthor = accountRoles.hasAuthor;
    this.selectedAccountHasAssignmentManager =
      accountRoles.hasAssignmentManager;
  }

  private setInitialTab(): void {
    const fromSimulationDetails = this.route?.snapshot?.queryParamMap?.get('fromSimulationDetails');
    
    if (fromSimulationDetails === 'true') {
      this.currentTab = AssignmentConstants.SIMULATIONS_TAB;

      setTimeout(() => {
        this.selectSimulationsTab();
      }, 100);
    } else if (this.shouldShowAssignmentsTab) {
      this.currentTab = AssignmentConstants.ASSIGNMENTS_TAB;
    } else if (this.shouldShowSimulationsTab) {
      this.currentTab = AssignmentConstants.SIMULATIONS_TAB;
    }
  }

  private prepareAccountOptions(): void {
    this.assignmentsTabOptions = this.mapAccountsToOptions(
      this.assignmentsTabAccounts
    );

    this.simulationsTabOptions = this.mapAccountsToOptions(
      this.simulationsTabAccounts
    );
  }

  private mapAccountsToOptions(
    accounts: IAccountInfo[]
  ): { label: string; value: string }[] {
    return accounts.map((account: IAccountInfo) => ({
      label: account.name,
      value: account.uuid,
    }));
  }

  private setSelectedAccount(): void {
    this.selectedAccountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const currentAccountUuid =
      SelfServeLocalStorageUtil.getCurrentAccountUuid();

    const uuidToMatch = this.selectedAccountUuid || currentAccountUuid;
    const currentOptions = this.options;

    const matchedOption = currentOptions.find(
      (opt) => opt.value === uuidToMatch
    );

    if (matchedOption) {
      this.selectedAccount = matchedOption;
      this.valueChange(matchedOption);
      this.getAllUsersWithTeams();
    } else if (currentOptions.length > 0) {
      this.selectedAccount = currentOptions[0];
      this.valueChange(currentOptions[0]);
      this.getAllUsersWithTeams();
    } else {
      this.resetAccountSelection();
    }

    this.determineSelectedAccountRoles();
  }

  private resetAccountSelection(): void {
    this.assignmentsTabOptions = [];
    this.simulationsTabOptions = [];
    this.selectedAccount = undefined;
    SelfServeLocalStorageUtil.setSelectedSelfServeAccountUuid('');
    this.selectedAccountUuid = '';
  }

  valueChange(selectedValue: string | { label: string; value: string }): void {
    let matchedOption: { label: string; value: string } | undefined;
    const currentOptions = this.options;

    if (typeof selectedValue === 'string') {
      matchedOption = currentOptions.find((opt) => opt.value === selectedValue);
    } else {
      matchedOption = selectedValue;
    }

    if (matchedOption) {
      this.selectedAccount = matchedOption;
      SelfServeLocalStorageUtil.setSelectedSelfServeAccountUuid(
        matchedOption.value
      );
      this.selectedAccountUuid = matchedOption.value;
      this.determineSelectedAccountRoles();
    }
  }

  selectionChange(event: QaiFloatingSelectOption | undefined) {
    this.getAllUsersWithTeams();
  }

  openModal(
    entityType: EntityType,
    actionType: EntityActionType,
    originalName: string = ''
  ) {
    if (
      entityType === EntityType.ASSIGNMENT &&
      actionType === EntityActionType.CREATE
    ) {
      this.openAssignmentModal();
      return;
    }

    this.modalOpen = true;
    this.modalEntityType = entityType;
    this.modalActionType = actionType;
    this.modalOriginalName = originalName;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.modalOriginalName = '';
  }

  get assignmentStatusLabel(): string | undefined {
    const statusCell = this.deleteContext?.rowData?.[
      'Assignment status'
    ] as any;
    return statusCell?.label;
  }

  onModalConfirmed(data: IConfirmActionPayload | string) {
    // Handle backward compatibility: if string is passed, treat as name only
    const name = typeof data === 'string' ? data : data.name;
    const moduleUuid = typeof data === 'string' ? undefined : data.moduleUuid;

    this.networkRequestInProgress = true;
    this.modalErrorStatus = undefined;

    if (this.modalEntityType === EntityType.SIMULATION) {
      if (
        this.modalActionType === EntityActionType.DUPLICATE &&
        this.simulationToDuplicate
      ) {
        this.duplicateSimulation(name, moduleUuid);
      } else {
        this.createNewSimulation(name, moduleUuid);
      }
    } else if (this.modalEntityType === EntityType.ASSIGNMENT) {
      this.duplicateAssignment(name);
    }
  }

  onDuplicateSimulation(event: { id: string; name: string; moduleUuid?: string }) {
    this.simulationToDuplicate = event;
    this.openModal(
      EntityType.SIMULATION,
      EntityActionType.DUPLICATE,
      event.name
    );
  }

  onDuplicateAssignment(event: { id: string; name: string }) {
    this.assignmentToDuplicateId = event.id;
    this.openModal(
      EntityType.ASSIGNMENT,
      EntityActionType.DUPLICATE,
      event.name
    );
  }

  handleCreateOptions(value: string) {
    const entityType =
      value === 'assignment' ? EntityType.ASSIGNMENT : EntityType.SIMULATION;
    this.openModal(entityType, EntityActionType.CREATE);
  }

  private createNewSimulation(name: string, moduleUuid?: string) {
    this.sendRequestToCreateSimulation(name, moduleUuid);
  }

  duplicateSimulation(newName: string, moduleUuid?: string) {
    if (!this.simulationToDuplicate) return;

    const finalModuleUuid = moduleUuid || this.simulationToDuplicate.moduleUuid || '';
    const payload = {
      callUuid: this.simulationToDuplicate.id,
      callTitle: newName,
      moduleUuid: finalModuleUuid,
    };

    this.authorService.duplicateSimulationAPI(payload).subscribe({
      next: (newSimulation: ISimulation) => {
        this.resetModalState();
        this.router.navigate(['simulations', newSimulation.uuid], {
          relativeTo: this.route,
          queryParams: { moduleUuid: finalModuleUuid },
        });
      },
      error: (err: any) => {
        console.error('Error duplicating simulation:', err);
        this.networkRequestInProgress = false;
        
        if (err?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
          this.modalErrorStatus = err.status;
        }
      },
      complete: () => {
        this.networkRequestInProgress = false;
      },
    });
  }

  duplicateAssignment(newName: string) {
    if (!this.assignmentToDuplicateId) return;

    const payload = {
      assignmentUuid: this.assignmentToDuplicateId,
      assignmentName: newName,
    };

    this.assignmentService.duplicateAssignmentAPI(payload).subscribe({
      next: (newAssignment: any) => {
        this.resetModalState();
        this.router.navigate(['assignments', newAssignment.uuid], {
          relativeTo: this.route,
        });
      },
      error: (err: any) => {
        console.error('Error duplicating assignment:', err);
        this.networkRequestInProgress = false;
        
        if (err?.status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS) {
          this.modalErrorStatus = err.status;
        }
      },
      complete: () => {
        this.networkRequestInProgress = false;
      },
    });
  }

  afterClose() {
    console.log('Simulation dialog is closed');
  }

  public resetModalState() {
    this.modalOpen = false;
    this.modalOriginalName = '';
    this.simulationToDuplicate = null;
    this.assignmentToDuplicateId = '';
    this.assignmentToDuplicateName = '';
    this.modalErrorStatus = undefined;
    this.cdRef.detectChanges();
  }

  sendRequestToCreateSimulation(name: string, moduleUuid?: string): void {
    this.authorService.createSimulationAPI(name, moduleUuid).subscribe({
      next: (res: ISimulation) => {
        console.log(res);
        const generalData: any = {
          simulationName: name,
        };
        if (moduleUuid) {
          generalData.moduleUuid = moduleUuid;
        }
        this.authorReactiveFormService.createSimulation(
          {
            general: generalData,
          },
          res.uuid
        );
        this.resetModalState();
        const queryParams: any = {};
        if (moduleUuid) {
          queryParams.moduleUuid = moduleUuid;
        }
        this.router.navigate(['simulations', res.uuid], {
          relativeTo: this.route,
          queryParams,
        });
      },
      error: (err) => {
        console.error(err);
        this.networkRequestInProgress = false;
        
        if (err?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
          this.modalErrorStatus = err.status;
        }
      },
      complete: () => {
        this.networkRequestInProgress = false;
      },
    });
  }

  onDeleteItem(
    type: EntityType.SIMULATION | EntityType.ASSIGNMENT,
    event: { id: string | number; rowData: TableRow; moduleUuid?: string }
  ) {
    this.deleteContext = {
      type,
      id: event.id,
      rowData: event.rowData,
      moduleUuid: event.moduleUuid,
    };
    this.isDeleteModalOpen = true;
    this.cdRef.detectChanges();
  }

  handleDeleteConfirmed() {
    if (!this.deleteContext) return;

    this.networkRequestInProgress = true;
    const { type, id, moduleUuid } = this.deleteContext;

    const delete$ =
      type === EntityType.SIMULATION
        ? this.authorService.deleteSimulationAPI(id as string, moduleUuid || '')
        : this.assignmentService.deleteStagedAssignmentAPI(id as string);

    delete$.subscribe({
      next: () => {
        this.closeDeleteModal();
        if (type === EntityType.SIMULATION) {
          this.simulationsRefreshKey++;
        } else if (type === EntityType.ASSIGNMENT) {
          this.assignmentsComponent.loadAssignments();
        }
      },
      error: (err: any) => {
        console.error(`Error deleting ${type}:`, err);
        this.closeDeleteModal();
      },
    });
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen = false;
    this.deleteContext = null;
    this.resetNetworkRequestState();
  }

  onDeleteSimulation(event: { id: string | number; rowData: TableRow }) {
    this.simulationToDelete = event;
    this.isDeleteModalOpen = true;
  }

  openAssignmentModal(uuid?: string) {
    this.assignmentModalComponent.moduleAssignmentUuid = uuid ?? null;
    this.isAssignmentModalVisible = true;
  }

  openAddSimulationToAssignmentModal() {
    // Get moduleUuid from assignment modal form if available
    if (this.assignmentModalComponent?.form) {
      this.moduleUuid = this.assignmentModalComponent.getModuleUuid();
    }
    this.addSimulationsToAssignmentModalOpen = true;
  }

  private resetModuleAssignmentUuid(): void {
    this.moduleAssignmentUuid = '';
  }

  private resetNetworkRequestState(): void {
    this.networkRequestInProgress = false;
  }

  closeAssignmentModal(): void {
    this.isAssignmentModalVisible = false;
    this.resetNetworkRequestState();
    this.resetModuleAssignmentUuid();
  }

  closeAddSimulationToAssignmentModal(): void {
    this.addSimulationsToAssignmentModalOpen = false;
    this.resetModuleAssignmentUuid();
  }

  closeAddSimulationAndOpenAssignmentModal(): void {
    this.addSimulationsToAssignmentModalOpen = false;
    this.openAssignmentModal(this.moduleAssignmentUuid);
  }

  openUserAssignmentModal(): void {
    this.isUserAssignmentModalVisible = true;
  }

  closeUserAssignmentModal(): void {
    this.isUserAssignmentModalVisible = false;
    this.resetModuleAssignmentUuid();
  }

  handlePostUserAssignmentModal() {
    this.isUserAssignmentModalVisible = false;
    this.router.navigate(['assignments', this.moduleAssignmentUuid], {
      relativeTo: this.route,
    });
  }

  handleSimulationAssigned() {
    this.addSimulationsToAssignmentModalOpen = false;
    this.openUserAssignmentModal();
  }

  handleAssignmentSave(assignment: IAssignment, openSimulationModal: boolean) {
    console.log('Assignment saved:', assignment);
    this.assignmentRequestInProgress = true;

    const payload = {
      ...assignment,
      startDate: toIsoDateString(assignment.startDate),
      endDate: toIsoDateString(assignment.endDate),
      showMissedFacts: assignment.showMissedFacts,
    };

    if (!this.moduleAssignmentUuid) {
      this.createAssignment(payload, openSimulationModal);
      return;
    }
    this.updateAssignment(
      this.moduleAssignmentUuid,
      payload,
      openSimulationModal
    );
  }

  private buildDefaultData(res: IAssignmentCreate) {
    return {
      details: {
        moduleAssignmentName: res.name,
        moduleUuid: res.moduleUuid,
        startDate: toDateInputString(res.startDate ?? ''),
        endDate: toDateInputString(res.endDate ?? ''),
        scoreCriteria: res.scoreCriteria,
        description: res.description,
        moduleAssignmentType: res.moduleAssignmentType,
        pauseEnabled: res.pauseEnabled,
        hideResultsForLearners: res.hideResultsForLearners,
        showMissedFacts: res.showMissedFacts,
      },
    };
  }

  private createAssignment(payload: IAssignment, openSimulationModal: boolean) {
    this.assignmentService.createAssignmentAPI(payload).subscribe({
      next: (res: IAssignmentCreate) => {
        const defaultData = this.buildDefaultData(res);
        this.assignmentReactiveFormService.createAssignment(
          defaultData,
          res.uuid
        );
        this.moduleAssignmentUuid = res.uuid;
        this.moduleUuid = res.moduleUuid || payload.moduleUuid || '';
        this.handlePostAssignmentSave(res, openSimulationModal);
        this.assignmentsComponent.loadAssignments();
      },  
      error: (err) => {
        console.error(err);
        if (err?.status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS) {
          this.assignmentReactiveFormService.setAssignmentNameConflict(true);
        }
        this.assignmentRequestInProgress = false;
      },
      complete: () => {
        this.assignmentRequestInProgress = false;
      },
    });
  }

  private updateAssignment(
    uuid: string,
    payload: IAssignment,
    openSimulationModal: boolean
  ) {
    this.assignmentService.updateAssignment(uuid, payload).subscribe({
      next: (res: IAssignmentCreate) => {
        this.moduleUuid = res.moduleUuid || payload.moduleUuid || '';
        this.handlePostAssignmentSave(res, openSimulationModal);
        this.assignmentsComponent.loadAssignments();
      },
      error: (err) => {
        console.error(err);
        this.assignmentRequestInProgress = false;
      },
      complete: () => {
        this.assignmentRequestInProgress = false;
      },
    });
  }

  private handlePostAssignmentSave(
    res: IAssignmentCreate,
    openSimulationModal: boolean
  ) {
    this.isAssignmentModalVisible = false;
    if (openSimulationModal) {
      this.openAddSimulationToAssignmentModal();
    } else {
      this.router.navigate(['assignments', res.uuid], {
        relativeTo: this.route,
      });
    }
  }

  onTabChange(tabId: string) {
    if (tabId === 'uniqueId1') {
      this.currentTab = AssignmentConstants.ASSIGNMENTS_TAB;
      if (this.route?.snapshot?.queryParams) {
        this.removeSimulationDetailsQuery();
      }
    } else if (tabId === 'uniqueId2') {
      this.currentTab = AssignmentConstants.SIMULATIONS_TAB;
    }

    this.setSelectedAccount();
  }

  private removeSimulationDetailsQuery(): void {
    if (!this.route?.snapshot?.queryParams) {
      return;
    }
    
    const currentParams = { ...this.route.snapshot.queryParams };
    if (currentParams['fromSimulationDetails']) {
      delete currentParams['fromSimulationDetails'];
      this.router.navigate([], {
        queryParams: currentParams,
        replaceUrl: true
      });
    }
  }

  private checkQueryParameters(): void {
    const fromSimulationDetails = this.route?.snapshot?.queryParamMap?.get('fromSimulationDetails');
    
    if (fromSimulationDetails === 'true' && this.shouldShowSimulationsTab) {
      this.currentTab = AssignmentConstants.SIMULATIONS_TAB;

      setTimeout(() => {
        this.selectSimulationsTab();
      }, 100);
      this.cdRef.detectChanges();
    }
  }

  private selectSimulationsTab(): void {
      this.tabsComponent?.selectTabById('uniqueId2');
  }

  isComingFromSimulationDetails(): boolean {
    const fromSimulationDetails = this.route?.snapshot?.queryParamMap?.get('fromSimulationDetails');
    return fromSimulationDetails === 'true';
  }

  getAllUsersWithTeams(): any {
    this.assignmentService.getAllUsersWithTeams().subscribe({
      next: (usersWithTeams: IUsersWithTeams) => {
        const allTeamsWithUsers =
          this.teamUserParserService.transformTeamUserData(usersWithTeams);
        SelfServeLocalStorageUtil.setTransformedTeamsWithUsers(
          JSON.stringify(allTeamsWithUsers)
        );
      },
      error: (err: any) => {
        console.error('Failed to users with teams', err);
      },
    });
  }
}
